import { listCourses } from "./courseHelpers"
import { listOrgEnrollments } from "./enrollmentHelpers"
import { listOrgCertificates } from "./certHelpers"
import { listOrgOrders } from "./orderHelpers"
import { listOrgProductViews } from "./productViewHelpers"
import { listOrgPageViews } from "./pageViewHelpers"
import { getRangeBounds, isWithin, percentChange } from "./dateRangeHelpers"

const countStagesForBounds = ({ paidCourseIds, pageViews, views, orders, enrollments, certificates, start, end }) => {
  const visitorIds = new Set(
    pageViews?.filter((v) => isWithin(v?.createdAt, start, end))?.map((v) => v?.visitorId)
  )
  const stageViews = views?.filter((v) => paidCourseIds?.has(v?.productId) && isWithin(v?.createdAt, start, end)) || []
  const stageOrders =
    orders?.filter(
      (o) => o?.status === "paid" && paidCourseIds?.has(o?.productId) && isWithin(o?.paidAt || o?.createdAt, start, end)
    ) || []
  const stageEnrollments =
    enrollments?.filter((e) => paidCourseIds?.has(e?.courseId) && isWithin(e?.enrolledAt, start, end)) || []
  const stageCompletions = stageEnrollments?.filter((e) => (e?.progressPercent || 0) >= 100) || []
  const stageCredentials =
    certificates?.filter((c) => paidCourseIds?.has(c?.courseId) && isWithin(c?.issueDate, start, end)) || []

  return {
    visitors: visitorIds.size,
    views: stageViews?.length,
    purchases: stageOrders?.length,
    enrollments: stageEnrollments?.length,
    completions: stageCompletions?.length,
    credentials: stageCredentials?.length,
  }
}

// Same 6-stage shape as the Learning Funnel, scoped to paid products only,
// now filterable by date range with real previous-period trend comparisons.
export const computeProductConversion = async (orgId, rangeKey = "30d") => {
  const [{ courses }, { enrollments }, { certificates }, { orders }, { views }, { pageViews }] = await Promise.all([
    listCourses(orgId),
    listOrgEnrollments(orgId),
    listOrgCertificates(orgId),
    listOrgOrders(orgId),
    listOrgProductViews(orgId),
    listOrgPageViews(orgId),
  ])

  const paidCourseIds = new Set(courses?.filter((c) => c?.pricing?.isFree === false)?.map((c) => c?.courseId) || [])
  const { start, end, prevStart, prevEnd } = getRangeBounds(rangeKey)

  const current = countStagesForBounds({ paidCourseIds, pageViews, views, orders, enrollments, certificates, start, end })
  const previous = countStagesForBounds({
    paidCourseIds,
    pageViews,
    views,
    orders,
    enrollments,
    certificates,
    start: prevStart,
    end: prevEnd,
  })

  const stageDefs = [
    { key: "visitors", label: "Visitors" },
    { key: "views", label: "Product Views" },
    { key: "purchases", label: "Purchases" },
    { key: "enrollments", label: "Enrollments" },
    { key: "completions", label: "Completions" },
    { key: "credentials", label: "Credentials" },
  ]

  const stages = stageDefs?.map((def) => ({
    key: def?.key,
    label: def?.label,
    value: current?.[def?.key],
    change: percentChange(current?.[def?.key], previous?.[def?.key]),
    tracked: def?.key === "visitors" || def?.key === "views" ? current?.[def?.key] > 0 : true,
  }))

  const visibleStages = stages?.filter((s) => s?.tracked)
  const withConversions = visibleStages?.map((stage, i) => {
    if (i === 0) return { ...stage, stepConversion: null }
    const prev = visibleStages?.[i - 1]
    const stepConversion = prev?.value > 0 ? Math.round((stage?.value / prev?.value) * 1000) / 10 : 0
    return { ...stage, stepConversion }
  })

  const topStage = visibleStages?.[0]
  const overallConversion =
    topStage?.value > 0 ? Math.round((current?.credentials / topStage?.value) * 1000) / 10 : 0
  const prevOverallConversion =
    previous?.[topStage?.key] > 0 ? Math.round((previous?.credentials / previous?.[topStage?.key]) * 1000) / 10 : 0

  return {
    stages: withConversions,
    hasPaidProducts: paidCourseIds?.size > 0,
    visitorsTracked: current?.visitors > 0,
    purchases: { value: current?.purchases, change: percentChange(current?.purchases, previous?.purchases) },
    overallConversion: { value: overallConversion, change: percentChange(overallConversion, prevOverallConversion) },
  }
}